// Board sub-grouping + filter option constants. Pure helpers (no JSX) split out of board.tsx.
import { LogisticsOrderRow } from "../types";

export const PROVIDER_OPTIONS = [
  { value: "", label: "Any carrier" },
  { value: "own_tms", label: "Own fleet (Loginext)" },
  { value: "porter", label: "Porter" },
  { value: "intercity", label: "Intercity / part-load" },
  { value: "supplier_managed", label: "Supplier delivers" },
];

export const GROUP_BY_OPTIONS = [
  { value: "trip", label: "Group by trip (vehicle)" },
  { value: "assignee", label: "Group by person" },
  { value: "none", label: "No sub-grouping" },
];

export interface RowGroup {
  key: string;
  label: string;
  rows: LogisticsOrderRow[];
}

const UNASSIGNED = "— Unassigned —";

function subGroupKey(row: LogisticsOrderRow, groupBy: string): string {
  if (groupBy === "assignee") return row.assigned_to_name?.trim() || UNASSIGNED;
  // The NAMED Loginext trip — one panel per actual run. Distinct from "trip" below, which keys on
  // the vehicle: over a multi-day range one truck runs several trips, and keying on the vehicle
  // folds them into a single panel that then has to list every trip name in its header.
  //
  // Only safe on a surface that asks the server for `has_trip`, where every row is guaranteed a
  // trip name — the Operations board has untrip-assigned rows and must keep using "trip", or they
  // would all collapse into one Unassigned panel. The vehicle + driver fallback below is belt and
  // braces: it keeps two unrelated trucks apart even if an unnamed row ever reaches here.
  if (groupBy === "trip_name") {
    return row.trip_name?.trim() || subGroupKey(row, "trip");
  }
  if (groupBy === "trip") {
    // A "trip" is the physical run — keyed on the VEHICLE (+ driver). NOT provider_ref: that's the
    // per-booking carrier order id, so keying on it would put every booking in its own group.
    return [row.vehicle_number, row.driver_name].filter(Boolean).join(" · ") || UNASSIGNED;
  }
  return "";
}

export function groupRows(rows: LogisticsOrderRow[], groupBy: string): RowGroup[] {
  if (groupBy === "none") return [{ key: "", label: "", rows }];
  const map = new Map<string, LogisticsOrderRow[]>();
  for (const r of rows) {
    const k = subGroupKey(r, groupBy);
    (map.get(k) ?? map.set(k, []).get(k)!).push(r);
  }
  // Unassigned last; the rest alphabetical.
  return Array.from(map.entries())
    .sort(([a], [b]) => {
      if (a === UNASSIGNED) return 1;
      if (b === UNASSIGNED) return -1;
      return a.localeCompare(b);
    })
    .map(([key, rs]) => ({ key, label: key, rows: rs }));
}
